import React, {useMemo} from 'react'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Text, ReferenceLine } from 'recharts';
import { Box } from '@mui/material';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

dayjs.extend(duration);

export const convert_seconds = (seconds) => {
  if (!seconds) return '00:00:00'

  const totalSeconds = Number(seconds)
  const totalDays = Math.floor(totalSeconds / (60 * 60 * 24))
  const remainder = totalSeconds % (60 * 60 * 24)

  const converted = dayjs.duration(remainder, 'seconds')

  if (totalDays > 0)
    return `${totalDays}d ${converted.format('HH:mm:ss')}`

  return converted.format('HH:mm:ss')
}

window.convert_seconds = convert_seconds

const formatData = (data, interval, isIntraday) => {
    const pre_ordered_list = Object.entries(data)
        .map(([key, value]) => [
            dayjs(value.time ? value.time * 1000 : key),
            { ...value, key }
        ])
        .toSorted((x, y) => x[0] - y[0])

    const actual_date = pre_ordered_list[pre_ordered_list.length - 1]

    const ordered_list = isIntraday
        ? pre_ordered_list.filter(([date]) =>
              date.isAfter(actual_date[0].subtract(Number(interval), "hours")) ||
              date.isSame(actual_date[0].subtract(Number(interval), "hours"))
          )
        : pre_ordered_list

    return {
        onbot: ordered_list.map(([, value]) => ({ x: value.key, y: value.onbot })),
        maxonbot: ordered_list.map(([, value]) => ({ x: value.key, y: value.max_onbot })),
        queue: ordered_list.map(([, value]) => ({ x: value.key, y: value.onqueue })),
        maxqueue: ordered_list.map(([, value]) => ({ x: value.key, y: value.max_onqueue })),
        onchat: ordered_list.map(([, value]) => ({ x: value.key, y: value.onchat })),
        maxonchat: ordered_list.map(([, value]) => ({ x: value.key, y: value.max_onchat })),
        tma: ordered_list.map(([, value]) => ({ x: value.key, y: value.tma })),
        maxtma: ordered_list.map(([, value]) => ({ x: value.key, y: value.max_tma })),
        tme: ordered_list.map(([, value]) => ({ x: value.key, y: value.tme })),
        maxtme: ordered_list.map(([, value]) => ({ x: value.key, y: value.max_tme })),
        tmr: ordered_list.map(([, value]) => ({ x: value.key, y: value.tmr })),
        maxtmr: ordered_list.map(([, value]) => ({ x: value.key, y: value.max_tmr })),
        notifications: ordered_list.map(([, value]) => ({ x: value.key, y: value.notifications ?? 0 })),
        receptives: ordered_list.map(([, value]) => ({ x: value.key, y: value.receptives ?? 0 }))
    }
}

const colorPallets = {
    'queue': '#8884d8',
    'maxqueue': '#84acd8',
    'onchat': '#216c22',
    'maxonchat': '#3a6c21',
    'tma': '#c95f50',
    'maxtma': '#c95050',
    'tme': '#96c950',
    'maxtme': '#b4c950',
    'tmr': '#b850c9',
    'maxtmr': '#9050c9',
    'onbot': '#5099c9',
    'maxonbot': '#50bdc9',
    "notifications": "#e3a857",
    "receptives": "#e3a857"
}

const AllCharts = ({ data, mode, interval, last_date, visibleCharts }) => {

    const isIntraday = !!Object.values(data)[0]?.time
    const formatted_data = useMemo(()=> formatData(data, interval, isIntraday), [data])

    return (
        <Box className="chart-container-grid" key={`${mode}-${Object.keys(visibleCharts).filter(k => visibleCharts[k]).join("-")}-${isIntraday ? "intraday" : "daily"}`}>
            {visibleCharts.queue_chats && isIntraday &&
                <Box className='chart-container queue'>
                    <Box sx={{ color: colorPallets.queue }} className='chart-fixed-text'>
                        {formatted_data.queue?.find(e => e.x === last_date)?.y}
                        <small>média</small>
                    </Box>
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                            data={formatted_data.queue}
                            margin={{
                                top: 0,
                                right: 0,
                                left: 0,
                                bottom: 0,
                            }}
                        >
                            <ReferenceLine x={last_date} stroke="green" />
                            <Legend margin={{ left: -60 }} iconSize={0} verticalAlign="top" align="center" height={36} />
                            <XAxis label={(isIntraday) ? "Hora" : "Dia"} hide dataKey="x" />
                            <YAxis name='test' allowDecimals={false} mirror={true} />
                            <Tooltip labelFormatter={(v) => (isIntraday) ? `Hora: ${v}` : `Dia: ${v}`} />
                            <Area strokeWidth={2} name='Chats em Fila' type={(isIntraday) ? "monotone" : "step"} dataKey="y" stroke={colorPallets.queue} fill={colorPallets.queue} />
                        </AreaChart>
                    </ResponsiveContainer>
                </Box>
            }
            {visibleCharts.max_queue_chats &&
                <Box className='chart-container queue'>
                    <Box sx={{ color: colorPallets.maxqueue }} className='chart-fixed-text'>
                        {Math.max(0, ...formatted_data.maxqueue.map(e=>e.y))}
                        <small>maior valor</small>
                    </Box>
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                            data={formatted_data.maxqueue}
                            margin={{
                                top: 0,
                                right: 0,
                                left: 0,
                                bottom: 0,
                            }}
                        >
                            <ReferenceLine x={last_date} stroke="green" />
                            <Legend margin={{ left: 30 }} iconSize={0} verticalAlign="top" align="center" height={36} />
                            <XAxis label={(isIntraday) ? "Hora" : "Dia"} hide dataKey="x" />
                            <YAxis name='test' allowDecimals={false} mirror={true} />
                            <Tooltip labelFormatter={(v) => (isIntraday) ? `Hora: ${v}` : `Dia: ${v}`}/>
                            <Area strokeWidth={2}name='Máximo de Chats em Fila' type={(isIntraday) ? "monotone" : "step"} dataKey="y" stroke={colorPallets.maxqueue} fill={colorPallets.maxqueue} />
                        </AreaChart>
                    </ResponsiveContainer>
                </Box>
            }
            {visibleCharts.ongoing_chats && isIntraday &&
                <Box className='chart-container queue'>
                    <Box sx={{ color: colorPallets.onchat }} className='chart-fixed-text'>
                        {formatted_data.onchat?.find(e => e.x === last_date)?.y}
                        <small>média</small>
                    </Box>
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                            data={formatted_data.onchat}
                            margin={{
                                top: 0,
                                right: 0,
                                left: 0,
                                bottom: 0,
                            }}
                        >
                            <ReferenceLine x={last_date} stroke="green" />
                            <Legend margin={{ left: 30 }} iconSize={0} verticalAlign="top" align="center" height={36} />
                            <XAxis label={(isIntraday) ? "Hora" : "Dia"} hide dataKey="x" />
                            <YAxis name='test' allowDecimals={false} mirror={true} />
                            <Tooltip labelFormatter={(v) => (isIntraday) ? `Hora: ${v}` : `Dia: ${v}`}/>
                            <Area strokeWidth={2}name='Chats em Atendimento' type={(isIntraday) ? "monotone" : "step"} dataKey="y" stroke={colorPallets.onchat} fill={colorPallets.onchat} />
                        </AreaChart>
                    </ResponsiveContainer>
                </Box>
            }
            {visibleCharts.max_ongoing_chats &&
                <Box className='chart-container queue'>
                    <Box sx={{ color: colorPallets.maxonchat }} className='chart-fixed-text'>
                        {Math.max(0, ...formatted_data.maxonchat.map(e=>e.y))}
                        <small>maior valor</small>
                    </Box>
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                            data={formatted_data.maxonchat}
                            margin={{
                                top: 0,
                                right: 0,
                                left: 0,
                                bottom: 0,
                            }}
                        >
                            <ReferenceLine x={last_date} stroke="green" />
                            <Legend margin={{ left: 30 }} iconSize={0} verticalAlign="top" align="center" height={36} />
                            <XAxis label={(isIntraday) ? "Hora" : "Dia"} hide dataKey="x" />
                            <YAxis name='test' allowDecimals={false} mirror={true} />
                            <Tooltip labelFormatter={(v) => (isIntraday) ? `Hora: ${v}` : `Dia: ${v}`}/>
                            <Area strokeWidth={2}name='Máximo de chats em Atendimento' type={(isIntraday) ? "monotone" : "step"} dataKey="y" stroke={colorPallets.maxonchat} fill={colorPallets.maxonchat} />
                        </AreaChart>
                    </ResponsiveContainer>
                </Box>
            }
            {visibleCharts.tma &&
                <Box className='chart-container queue'>
                    <Box sx={{ color: colorPallets.tma }} className='chart-fixed-text'>
                        {convert_seconds((isIntraday)
                            ? formatted_data.tma?.find(e => e.x === last_date)?.y
                            : (formatted_data.tma && formatted_data.tma.length > 0
                                ? formatted_data.tma.reduce((sum, obj) => sum + obj.y, 0) / formatted_data.tma.length
                                : 0
                            )
                        )}
                        <small>média</small>
                    </Box>
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                            data={formatted_data.tma}
                            margin={{
                                top: 0,
                                right: 0,
                                left: 0,
                                bottom: 0,
                            }}
                        >
                            <ReferenceLine x={last_date} stroke="green" />
                            <Legend margin={{ left: 30 }} iconSize={0} verticalAlign="top" align="center" height={36} />
                            <XAxis label={(isIntraday) ? "Hora" : "Dia"} hide dataKey="x" />
                            <YAxis hide name='test' allowDecimals={false} mirror={true} />
                            <Tooltip labelFormatter={(v) => (isIntraday) ? `Hora: ${v}` : `Dia: ${v}`} formatter={(value, name, props) => convert_seconds(value)} />
                            <Area strokeWidth={2}name='TMA (Fila)' type={(isIntraday) ? "monotone" : "step"} dataKey="y" stroke={colorPallets.tma} fill={colorPallets.tma} />
                        </AreaChart>
                    </ResponsiveContainer>
                </Box>
            }
            {visibleCharts.max_tma &&
                <Box className='chart-container queue'>
                    <Box sx={{ color: colorPallets.maxtma }} className='chart-fixed-text'>
                        {convert_seconds(Math.max(0, ...formatted_data.maxtma.map(e=>e.y)))}
                        <small>maior valor</small>
                    </Box>
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                            data={formatted_data.maxtma}
                            margin={{
                                top: 0,
                                right: 0,
                                left: 0,
                                bottom: 0,
                            }}
                        >
                            <ReferenceLine x={last_date} stroke="green" />
                            <Legend margin={{ left: 30 }} iconSize={0} verticalAlign="top" align="center" height={36} />
                            <XAxis label={(isIntraday) ? "Hora" : "Dia"} hide dataKey="x" />
                            <YAxis hide name='test' allowDecimals={false} mirror={true} />
                            <Tooltip labelFormatter={(v) => (isIntraday) ? `Hora: ${v}` : `Dia: ${v}`} formatter={(value, name, props) => convert_seconds(value)}/>
                            <Area strokeWidth={2}name='Maior TMA (Fila)' type={(isIntraday) ? "monotone" : "step"} dataKey="y" stroke={colorPallets.maxtma} fill={colorPallets.maxtma} />
                        </AreaChart>
                    </ResponsiveContainer>
                </Box>
            }
            {visibleCharts.tme &&
                <Box className='chart-container queue'>
                    <Box sx={{ color: colorPallets.tme }} className='chart-fixed-text'>
                        {convert_seconds((isIntraday)
                            ? formatted_data.tme?.find(e => e.x === last_date)?.y
                            : (formatted_data.tme && formatted_data.tme.length > 0
                                ? formatted_data.tme.reduce((sum, obj) => sum + obj.y, 0) / formatted_data.tme.length
                                : 0
                            )
                        )}
                        <small>média</small>
                    </Box>
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                            data={formatted_data.tme}
                            margin={{
                                top: 0,
                                right: 0,
                                left: 0,
                                bottom: 0,
                            }}
                        >
                            <ReferenceLine x={last_date} stroke="green" />
                            <Legend margin={{ left: 30 }} iconSize={0} verticalAlign="top" align="center" height={36} />
                            <XAxis label={(isIntraday) ? "Hora" : "Dia"} hide dataKey="x" />
                            <YAxis hide name='test' allowDecimals={false} mirror={true} />
                            <Tooltip labelFormatter={(v) => (isIntraday) ? `Hora: ${v}` : `Dia: ${v}`} formatter={(value, name, props) => convert_seconds(value)}/>
                            <Area strokeWidth={2}name='TME' type={(isIntraday) ? "monotone" : "step"} dataKey="y" stroke={colorPallets.tme} fill={colorPallets.tme} />
                        </AreaChart>
                    </ResponsiveContainer>
                </Box>
            }
            {visibleCharts.max_tme &&
                <Box className='chart-container queue'>
                    <Box sx={{ color: colorPallets.maxtme }} className='chart-fixed-text'>
                        {convert_seconds(Math.max(0, ...formatted_data.maxtme.map(e=>e.y)))}
                        <small>maior valor</small>
                    </Box>
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                            data={formatted_data.maxtme}
                            margin={{
                                top: 0,
                                right: 0,
                                left: 0,
                                bottom: 0,
                            }}
                        >
                            <ReferenceLine x={last_date} stroke="green" />
                            <Legend margin={{ left: 30 }} iconSize={0} verticalAlign="top" align="center" height={36} />
                            <XAxis label={(isIntraday) ? "Hora" : "Dia"} hide dataKey="x" />
                            <YAxis hide name='test' allowDecimals={false} mirror={true} />
                            <Tooltip labelFormatter={(v) => (isIntraday) ? `Hora: ${v}` : `Dia: ${v}`}formatter={(value, name, props) => convert_seconds(value)}/>
                            <Area strokeWidth={2}name='Maior TME' type={(isIntraday) ? "monotone" : "step"} dataKey="y" stroke={colorPallets.maxtme} fill={colorPallets.maxtme} />
                        </AreaChart>
                    </ResponsiveContainer>
                </Box>
            }
            {visibleCharts.tmr &&
                <Box className='chart-container queue'>
                    <Box sx={{ color: colorPallets.tmr }} className='chart-fixed-text'>
                        {convert_seconds((isIntraday)
                            ? formatted_data.tmr?.find(e => e.x === last_date)?.y
                            : (formatted_data.tmr && formatted_data.tmr.length > 0
                                ? formatted_data.tmr.reduce((sum, obj) => sum + obj.y, 0) / formatted_data.tmr.length
                                : 0
                            )
                        )}
                        <small>média</small>
                    </Box>
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                            data={formatted_data.tmr}
                            margin={{
                                top: 0,
                                right: 0,
                                left: 0,
                                bottom: 0,
                            }}
                        >
                            <ReferenceLine x={last_date} stroke="green" />
                            <Legend margin={{ left: 30 }} iconSize={0} verticalAlign="top" align="center" height={36} />
                            <XAxis label={(isIntraday) ? "Hora" : "Dia"} hide dataKey="x" />
                            <YAxis hide name='test' allowDecimals={false} mirror={true} />
                            <Tooltip labelFormatter={(v) => (isIntraday) ? `Hora: ${v}` : `Dia: ${v}`} formatter={(value, name, props) => convert_seconds(value)} />
                            <Area strokeWidth={2}name='TMR' type={(isIntraday) ? "monotone" : "step"} dataKey="y" stroke={colorPallets.tmr} fill={colorPallets.tmr} />
                        </AreaChart>
                    </ResponsiveContainer>
                </Box>
            }
            {visibleCharts.max_tmr &&
                <Box className='chart-container queue'>
                    <Box sx={{ color: colorPallets.maxtmr }} className='chart-fixed-text'>
                        {convert_seconds(Math.max(0, ...formatted_data.maxtmr.map(e=>e.y)))}
                        <small>maior valor</small>
                    </Box>
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                            data={formatted_data.maxtmr}
                            margin={{
                                top: 0,
                                right: 0,
                                left: 0,
                                bottom: 0,
                            }}
                        >
                            <ReferenceLine x={last_date} stroke="green" />
                            <Legend margin={{ left: 30 }} iconSize={0} verticalAlign="top" align="center" height={36} />
                            <XAxis label={(isIntraday) ? "Hora" : "Dia"} hide dataKey="x" />
                            <YAxis hide name='test' allowDecimals={false} mirror={true} />
                            <Tooltip labelFormatter={(v) => (isIntraday) ? `Hora: ${v}` : `Dia: ${v}`} formatter={(value, name, props) => convert_seconds(value)} />
                            <Area strokeWidth={2}name='Maior TMR' type={(isIntraday) ? "monotone" : "step"} dataKey="y" stroke={colorPallets.maxtmr} fill={colorPallets.maxtmr} />
                        </AreaChart>
                    </ResponsiveContainer>
                </Box>
            }
            {mode === '__all__' &&
                <>
                    {visibleCharts.bot_chats && isIntraday &&
                        <Box className='chart-container queue'>
                            <Box sx={{ color: colorPallets.onbot }} className='chart-fixed-text'>
                                {formatted_data.onbot?.find(e => e.x === last_date)?.y}
                                <small>média</small>
                            </Box>
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart
                                    width={500}
                                    height={400}
                                    data={formatted_data.onbot}
                                    margin={{
                                        top: 0,
                                        right: 0,
                                        left: 0,
                                        bottom: 0,
                                    }}
                                >
                                    <ReferenceLine x={last_date} stroke="green" />
                                    <Legend margin={{ left: 30 }} iconSize={0} verticalAlign="top" align="center" height={36} />
                                    <XAxis label={(isIntraday) ? "Hora" : "Dia"} hide dataKey="x" />
                                    <YAxis name='test' allowDecimals={false} mirror={true} />
                                    <Tooltip labelFormatter={(v) => (isIntraday) ? `Hora: ${v}` : `Dia: ${v}`} />
                                    <Area strokeWidth={2}name='Chats no Bot' type={(isIntraday) ? "monotone" : "step"} dataKey="y" stroke={colorPallets.onbot} fill={colorPallets.onbot} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </Box>
                    }
                    {visibleCharts.max_bot_chats &&
                        <Box className='chart-container queue'>
                            <Box sx={{ color: colorPallets.maxonbot }} className='chart-fixed-text'>
                                {Math.max(0, ...formatted_data.maxonbot.map(e=>e.y))}
                                <small>maior valor</small>
                            </Box>
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart
                                    width={500}
                                    height={400}
                                    data={formatted_data.maxonbot}
                                    margin={{
                                        top: 0,
                                        right: 0,
                                        left: 0,
                                        bottom: 0,
                                    }}
                                >
                                    <ReferenceLine x={last_date} stroke="green" />
                                    <Legend margin={{ left: 30 }} iconSize={0} verticalAlign="top" align="center" height={36} />
                                    <XAxis label={(isIntraday) ? "Hora" : "Dia"} hide dataKey="x" />
                                    <YAxis name='test' allowDecimals={false} mirror={true} />
                                    <Tooltip labelFormatter={(v) => (isIntraday) ? `Hora: ${v}` : `Dia: ${v}`}/>
                                    <Area strokeWidth={2}name='Máximo de Chats no Bot' type={(isIntraday) ? "monotone" : "step"} dataKey="y" stroke={colorPallets.maxonbot} fill={colorPallets.maxonbot} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </Box>
                    }
                </>
            }
            {visibleCharts.whatsapp_active &&
                <Box className='chart-container queue'>
                    <Box sx={{ color: colorPallets.notifications }} className='chart-fixed-text'>
                        {(() => {
                            const notifications = formatted_data.notifications || []
                            if (notifications.length === 0) return 0
                            if (isIntraday)
                                return Math.max(...notifications.map(e => e.y))
                            return notifications.reduce((sum, obj) => sum + obj.y, 0)
                        })()}
                        <small>total</small>
                    </Box>
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                            data={formatted_data.notifications}
                            margin={{
                                top: 0,
                                right: 0,
                                left: 0,
                                bottom: 0,
                            }}
                        >
                            <ReferenceLine x={last_date} stroke="green" />
                            <Legend margin={{ left: 30 }} iconSize={0} verticalAlign="top" align="center" height={36} />
                            <XAxis label={(isIntraday) ? "Hora" : "Dia"} hide dataKey="x" />
                            <YAxis name='test' allowDecimals={false} mirror={true} />
                            <Tooltip labelFormatter={(v) => (isIntraday) ? `Hora: ${v}` : `Dia: ${v}`}/>
                            <Area strokeWidth={2} name='WhatsApp (Ativos)' type={(isIntraday) ? "monotone" : "step"} dataKey="y" stroke={colorPallets.notifications} fill={colorPallets.notifications} />
                        </AreaChart>
                    </ResponsiveContainer>
                </Box>
            }
            {visibleCharts.whatsapp_receptive &&
                <Box className='chart-container queue'>
                    <Box sx={{ color: colorPallets.receptives }} className='chart-fixed-text'>
                        {(() => {
                            const receptives = formatted_data.receptives || []
                            if (receptives.length === 0) return 0
                            if (isIntraday)
                                return Math.max(...receptives.map(e => e.y))
                            return receptives.reduce((sum, obj) => sum + obj.y, 0)
                        })()}
                        <small>total</small>
                    </Box>
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                            data={formatted_data.receptives}
                            margin={{
                                top: 0,
                                right: 0,
                                left: 0,
                                bottom: 0,
                            }}
                        >
                            <ReferenceLine x={last_date} stroke="green" />
                            <Legend margin={{ left: 30 }} iconSize={0} verticalAlign="top" align="center" height={36} />
                            <XAxis label={(isIntraday) ? "Hora" : "Dia"} hide dataKey="x" />
                            <YAxis name='test' allowDecimals={false} mirror={true} />
                            <Tooltip labelFormatter={(v) => (isIntraday) ? `Hora: ${v}` : `Dia: ${v}`}/>
                            <Area strokeWidth={2} name='WhatsApp (Receptivos)' type={(isIntraday) ? "monotone" : "step"} dataKey="y" stroke={colorPallets.receptives} fill={colorPallets.receptives} />
                        </AreaChart>
                    </ResponsiveContainer>
                </Box>
            }
        </Box>
    )
}

export default AllCharts